import { catchError, firstValueFrom, Observable, retry, tap, throwError, timer } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  JoinSessionResponse,
  JoinSessionResponseSchema,
  RefreshTokenResponse,
  RefreshTokenResponseSchema,
  SHARED_SESSION_STORAGE_KEYS,
} from '../shared/models/shared-session.model';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class SharedSessionAuthService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.speaksee.apiUrl}/v1/shared-sessions`;
  private timerId: ReturnType<typeof setTimeout> | null = null;
  private renewalInFlight: Promise<boolean> | null = null;

  join(code: string): Observable<JoinSessionResponse> {
    const cleanCode = code.replace(/[\s-]/g, '');
    return this.http
      .post<unknown>(`${this.baseUrl}/join`, { code: cleanCode })
      .pipe(
        // Per BE handover v2: transient 500 (token-family write failure) is retry-safe.
        retry({
          count: 1,
          delay: (err) =>
            err instanceof HttpErrorResponse && err.status === 500
              ? timer(1000)
              : throwError(() => err),
        }),
        tap((raw) => {
          const parsed = JoinSessionResponseSchema.parse(raw);
          localStorage.setItem(SHARED_SESSION_STORAGE_KEYS.AUTH_MODE, 'shared-session');
          localStorage.setItem(SHARED_SESSION_STORAGE_KEYS.ACCESS_TOKEN, parsed.access_token);
          localStorage.setItem(SHARED_SESSION_STORAGE_KEYS.REFRESH_TOKEN, parsed.refresh_token);
          localStorage.setItem(SHARED_SESSION_STORAGE_KEYS.DEVICE_UUID, parsed.device_uuid);
          localStorage.setItem(SHARED_SESSION_STORAGE_KEYS.SESSION_ID, parsed.session_id);
          if (parsed.starts_at) {
            localStorage.setItem(SHARED_SESSION_STORAGE_KEYS.STARTS_AT, parsed.starts_at);
          }
          localStorage.setItem(SHARED_SESSION_STORAGE_KEYS.JOIN_CODE, cleanCode);
          this.scheduleRefresh();
        }),
      ) as Observable<JoinSessionResponse>;
  }

  isAuthenticated(): boolean {
    return (
      localStorage.getItem(SHARED_SESSION_STORAGE_KEYS.AUTH_MODE) === 'shared-session' &&
      !!localStorage.getItem(SHARED_SESSION_STORAGE_KEYS.ACCESS_TOKEN)
    );
  }

  getAccessToken(): string | null {
    return localStorage.getItem(SHARED_SESSION_STORAGE_KEYS.ACCESS_TOKEN);
  }

  getDeviceUuid(): string | null {
    return localStorage.getItem(SHARED_SESSION_STORAGE_KEYS.DEVICE_UUID);
  }

  flushTokens(): void {
    for (const key of Object.values(SHARED_SESSION_STORAGE_KEYS)) {
      localStorage.removeItem(key);
    }
  }

  refresh(): Observable<RefreshTokenResponse> {
    const rt = localStorage.getItem(SHARED_SESSION_STORAGE_KEYS.REFRESH_TOKEN);
    if (!rt) {
      return throwError(() => new Error('No refresh token'));
    }
    return this.http
      .post<unknown>(`${this.baseUrl}/refresh`, { refresh_token: rt })
      .pipe(
        tap((raw) => {
          const parsed = RefreshTokenResponseSchema.parse(raw);
          localStorage.setItem(SHARED_SESSION_STORAGE_KEYS.ACCESS_TOKEN, parsed.access_token);
          localStorage.setItem(SHARED_SESSION_STORAGE_KEYS.REFRESH_TOKEN, parsed.refresh_token);
        }),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 400 || err.status === 401) {
            localStorage.removeItem(SHARED_SESSION_STORAGE_KEYS.ACCESS_TOKEN);
            localStorage.removeItem(SHARED_SESSION_STORAGE_KEYS.REFRESH_TOKEN);
          }
          return throwError(() => err);
        }),
      ) as Observable<RefreshTokenResponse>;
  }

  scheduleRefresh(): void {
    this.cancelRefresh();
    const token = this.getAccessToken();
    if (!token) return;
    const exp = this.decodeExp(token);
    if (!exp) return;
    const nowMs = Date.now();
    const expMs = exp * 1000;
    const fireInMs = Math.max(expMs - nowMs - 60_000, 5_000);
    this.timerId = setTimeout(() => {
      this.refresh().subscribe({
        next: () => this.scheduleRefresh(),
        error: () => undefined,
      });
    }, fireInMs);
  }

  cancelRefresh(): void {
    if (this.timerId !== null) clearTimeout(this.timerId);
    this.timerId = null;
  }

  /** True when auth_mode is 'shared-session', regardless of whether a valid token is present. */
  isActive(): boolean {
    return localStorage.getItem(SHARED_SESSION_STORAGE_KEYS.AUTH_MODE) === 'shared-session';
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(SHARED_SESSION_STORAGE_KEYS.REFRESH_TOKEN);
  }

  getJoinCode(): string | null {
    return localStorage.getItem(SHARED_SESSION_STORAGE_KEYS.JOIN_CODE);
  }

  /** True if there is no access token, it cannot be decoded, or it expires within `skewMs`. */
  isTokenExpired(skewMs = 10_000): boolean {
    const token = this.getAccessToken();
    if (!token) return true;
    const exp = this.decodeExp(token);
    if (!exp) return true;
    return exp * 1000 <= Date.now() + skewMs;
  }

  /**
   * Guarantees a usable access token or reports terminal failure.
   * Chain: valid token -> refresh_token -> join code (soft re-join, new identity) -> fail.
   * Concurrent callers share a single in-flight renewal.
   */
  ensureValidToken(): Promise<boolean> {
    if (this.getAccessToken() && !this.isTokenExpired()) {
      // Arm the proactive timer once (e.g. on app load with a still-valid token)
      // without re-arming on every call, so frequent callers can't keep resetting it.
      if (this.timerId === null) this.scheduleRefresh();
      return Promise.resolve(true);
    }
    if (!this.renewalInFlight) {
      this.renewalInFlight = this.runRenewalChain().finally(() => {
        this.renewalInFlight = null;
      });
    }
    return this.renewalInFlight;
  }

  private async runRenewalChain(): Promise<boolean> {
    if (this.getRefreshToken()) {
      try {
        await firstValueFrom(this.refresh());
        this.scheduleRefresh();
        return true;
      } catch {
        // refresh() already cleared the access/refresh tokens (join code kept).
      }
    }
    const code = this.getJoinCode();
    if (code) {
      try {
        await firstValueFrom(this.join(code));
        this.scheduleRefresh();
        return true;
      } catch {
        console.warn('[SharedSession] Join-code fallback failed; session ended or code no longer valid.');
      }
    }
    this.flushTokens();
    return false;
  }

  private decodeExp(jwt: string): number | null {
    try {
      const segments = jwt.split('.');
      if (segments.length < 2) return null;
      const payload = segments[1];
      const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return typeof decoded.exp === 'number' ? decoded.exp : null;
    } catch {
      return null;
    }
  }
}
